import type { RunRowLike } from "@/types/RunRow"

/**
 * Whether a result row is a simulation still in flight.
 *
 * There is no status column on a simulation: audio and an end time are both
 * written when it finishes, so a row that has started, has not ended, and has no
 * stored recording is one that is still running.
 */
export function isSimulationLive(item: RunRowLike): boolean {
  return Boolean(item.simulation_start_time) && !isSimulationFinished(item)
}

/**
 * Whether live audio can be opened for this row.
 *
 * A listener can only join after the simulation worker has started watching
 * for room requests.
 */
export function isSimulationListenable(item: RunRowLike): boolean {
  return isSimulationLive(item)
}

function isSimulationFinished(item: RunRowLike): boolean {
  return Boolean(
    item.simulation_end_time ||
    item.simulation_s3_bucket ||
    item.simulation_s3_key,
  )
}

/**
 * Whether the row could still go (or stay) live: not finished, and not
 * parked on a terminal error. True before the simulation has even started —
 * this is the "worth watching" predicate that drives polling and the
 * pre-live in-progress UI, where `isSimulationLive` only flips once a start
 * time exists.
 */
export function canSimulationGoLive(item: RunRowLike): boolean {
  if (isSimulationFinished(item)) {
    return false
  }
  const errorStatus = item.error_status?.toUpperCase() ?? ""
  return (
    !errorStatus.includes("ERROR") &&
    !errorStatus.includes("FAILED") &&
    !errorStatus.includes("CANCELLED")
  )
}
